// ══════════════════════════════════════════════════════════════════════════════════════════════
//  t341 · ABRIR EL ASISTENTE LucIA DEL EQUIPO DESDE CUALQUIER SITIO (21-oct-2026)
// ══════════════════════════════════════════════════════════════════════════════════════════════
//
//  El panel vive en components/asistenteLucia/PanelAsistenteLucia.jsx, montado UNA vez en App.jsx detrás del interruptor
//  «asistente-lucia». Quien quiera abrirlo (el menú de la foto de Layout.jsx, y el botón flotante con LucIA en 3D que hace
//  otro agente) solo llama a `abrirAsistenteLucia()`: lanza un evento de la ventana y el panel lo recoge. Con la función
//  apagada no hay panel montado y el evento no hace nada.
//
//  `abrirAsistenteLucia({ pregunta })`: abre el panel y, si se le pasa, deja esa pregunta escrita en el cuadro (sin
//  mandarla: la manda la persona).
//
//  Y la forma de pintar lo que contesta LucIA sin dejar pasar HTML (`piezasDeLinea`): un texto con **negrita** y enlaces
//  [Nombre](/ruta), solo a pantallas de la propia app. Sin dependencias: lo cargan las guardias de verdad.
//  (t430: y lo que cuenta como «sí» y como «no» dichos, de shared/asistente-lucia-confirmar.json, la lista que usa el servidor.)

import CONFIRMAR from '../../../shared/asistente-lucia-confirmar.json' with { type: 'json' }
import { t as i18nT, marca } from './i18n.js'   // B-2259 · los textos, en el idioma de quien mira (utils/i18n)

export const EVENTO_ABRIR_ASISTENTE_LUCIA = 'biango-abrir-asistente-lucia'
export const EVENTO_CERRAR_ASISTENTE_LUCIA = 'biango-cerrar-asistente-lucia'
// El aviso que lanza el botón de LucIA en 3D de la esquina (t340, utils/luciaEnLaEsquina.jsx · AVISO_ABRIR_ASISTENTE): el panel lo
// ATIENDE (`detail.atendido = true`) y se abre; si no hay panel que lo atienda (la función apagada), ese botón abre el soporte.
export const AVISO_DEL_BOTON_DE_LUCIA = 'biango:abrir-asistente-lucia'

// t345 · LA CARA DE LucIA EN LA CABECERA DEL PANEL (el dueño, 21-oct: «el icono del robot de arriba sustitúyelo por
// LucIA»). La MISMA imagen quieta que el botón de la esquina (utils/luciaEnLaEsquina.jsx · POSTER_LUCIA, lo mira su
// guardia), recortada en un círculo a la cabeza: sin 3D dentro del panel, para no gastar.
export const IMAGEN_LUCIA = '/lucia/lucia-quieta.webp?v=1'
// El recorte: la imagen (320×320) al 179 % del círculo y corrida para que se vea la cabeza (x 70-249, y 10-189).
export const RECORTE_CARA_LUCIA = Object.freeze({ width: '179%', height: '179%', left: '-39%', top: '-6%' })

// t369 · DESDE LA FICHA, UNA CITA O UN HUECO DE LA AGENDA (fase 6): `abrirAsistenteLucia({ contexto, sitio })` abre el panel ya
// con ESE paciente, ESA cita o ESE hueco (solo ids, que el servidor comprueba: contextoLimpio) y con las sugerencias de ese
// sitio («ficha», «cita» o «hueco»; SITIOS_LUCIA). Sin ellos, como siempre.
export function abrirAsistenteLucia(detalle) {
  try {
    const d = detalle && typeof detalle === 'object' ? detalle : {}
    const contexto = contextoLimpio(d.contexto)
    const sitio = SITIOS_LUCIA.includes(d.sitio) ? d.sitio : null
    window.dispatchEvent(new CustomEvent(EVENTO_ABRIR_ASISTENTE_LUCIA, { detail: {
      pregunta: typeof d.pregunta === 'string' ? d.pregunta.slice(0, 2000) : '',
      ...(Object.keys(contexto).length ? { contexto } : {}),
      ...(sitio ? { sitio } : {}),
    } }))
  } catch (_) { /* sin ventana (una guardia) */ }
}

export function cerrarAsistenteLucia() {
  try { window.dispatchEvent(new CustomEvent(EVENTO_CERRAR_ASISTENTE_LUCIA)) } catch (_) { /* sin ventana */ }
}

// Un enlace solo vale si es de la propia app: empieza por «/» y no por «//» (eso sería otra web), sin «javascript:» ni nada raro.
export function rutaInterna(ruta) {
  const r = String(ruta || '').trim()
  if (!/^\/(?!\/)[A-Za-z0-9\-._~/?=&%#:+,]*$/.test(r)) return null
  return r
}

// Una línea de texto en piezas: { t: 'texto' | 'negrita' | 'enlace', v, ruta? }. Lo que no es una pieza reconocida es texto.
export function piezasDeLinea(linea) {
  const s = String(linea == null ? '' : linea)
  const out = []
  const re = /\*\*([^*]+)\*\*|\[([^\]]{1,120})\]\(([^)\s]{1,300})\)/g
  let pos = 0
  let m
  while ((m = re.exec(s))) {
    if (m.index > pos) out.push({ t: 'texto', v: s.slice(pos, m.index) })
    if (m[1] != null) out.push({ t: 'negrita', v: m[1] })
    else {
      const ruta = rutaInterna(m[3])
      if (ruta) out.push({ t: 'enlace', v: m[2], ruta })
      else out.push({ t: 'texto', v: m[2] })
    }
    pos = m.index + m[0].length
  }
  if (pos < s.length) out.push({ t: 'texto', v: s.slice(pos) })
  return out
}

// El texto entero en líneas: { lista: bool, piezas }. Una línea que empieza por «- » o «· » es un punto de una lista.
export function lineasDeRespuesta(texto) {
  return String(texto == null ? '' : texto).replace(/\r\n/g, '\n').split('\n').map((l) => {
    const lista = /^\s*[-·*]\s+/.test(l)
    return { lista, piezas: piezasDeLinea(lista ? l.replace(/^\s*[-·*]\s+/, '') : l) }
  })
}

// ══════════════════════════════════════════════════════════════════════════════════════════════
//  t345 · EL PANEL REDISEÑADO (21-oct-2026): lo que no es pintar, aquí (lo cargan las guardias de verdad)
// ══════════════════════════════════════════════════════════════════════════════════════════════
//  El dueño: un panel lateral que NO es una ventana (sin fondo oscuro ni desenfoque: se trabaja en la agenda o en la ficha
//  con él abierto), que entra y sale con animación, con el ancho a gusto de cada uno, y con accesos rápidos y tarjetas.
//  (t452: y, cuando se quiera, ampliado a todo el ancho de la pantalla: PANEL_AMPLIADO.)

// EL SALUDO del principio de la conversación (sin emojis: en Biango no se usan).
export const SALUDO_LUCIA = marca('utils.asistenteLucia.hola_soy_lucia_tu_asistente', 'Hola. Soy LucIA, tu asistente de la clínica. Puedo ayudarte a gestionar citas, buscar pacientes, consultar historiales y mucho más. ¿En qué puedo ayudarte?')
export const LEMA_LUCIA = marca('utils.asistenteLucia.te_ayudo_con_citas_pacientes', 'Te ayudo con citas, pacientes, tratamientos y cualquier consulta de la clínica.')

// LOS ACCESOS RÁPIDOS de debajo del saludo (rejilla de 2×2). Cada uno manda su pregunta, o abre un mini formulario dentro del
// chat y manda la pregunta con lo escrito. Cambiar uno es cambiar su línea.
// t349 (tanda 2) · «Nueva cita» ocupa el sitio que guardaba «Citas de hoy» (el del diseño del dueño: arriba a la derecha): un
// mini formulario con el paciente, el tratamiento y cuándo, y LucIA la prepara con su tarjeta y «Hacerlo». Las citas de hoy se le
// siguen preguntando escribiendo.
export const ACCESOS_RAPIDOS = Object.freeze([
  Object.freeze({ id: 'buscar_paciente', get texto() { return i18nT('utils.asistenteLucia.buscar_paciente', 'Buscar paciente') }, icono: 'user',
    formulario: Object.freeze({ get etiqueta() { return i18nT('utils.asistenteLucia.nombre_n_de_historia_o', 'Nombre, nº de historia o teléfono') }, boton: marca('utils.asistenteLucia.buscar', 'Buscar'), pregunta: (v) => i18nT('utils.asistenteLucia.busca_al_paciente', 'Busca al paciente {{v}}', { v }) }) }),
  Object.freeze({ id: 'nueva_cita', get texto() { return i18nT('utils.asistenteLucia.nueva_cita', 'Nueva cita') }, icono: 'calendar',
    formulario: Object.freeze({ get etiqueta() { return i18nT('utils.asistenteLucia.paciente_tratamiento_y_cuando', 'Paciente, tratamiento y cuándo') }, boton: marca('utils.asistenteLucia.pedirsela', 'Pedírsela'), pregunta: (v) => i18nT('utils.asistenteLucia.dale_una_cita_a', 'Dale una cita a {{v}}', { v }) }) }),
  Object.freeze({ id: 'historial', get texto() { return i18nT('utils.asistenteLucia.consultar_historial', 'Consultar historial') }, icono: 'file',
    formulario: Object.freeze({ get etiqueta() { return i18nT('utils.asistenteLucia.nombre_o_n_de_historia', 'Nombre o nº de historia del paciente') }, boton: marca('utils.asistenteLucia.consultar', 'Consultar'), pregunta: (v) => i18nT('utils.asistenteLucia.ensename_el_historial_del_paciente', 'Enséñame el historial del paciente {{v}}', { v }) }) }),
  Object.freeze({ id: 'tareas', get texto() { return i18nT('utils.asistenteLucia.tareas_pendientes', 'Tareas pendientes') }, icono: 'bell',
    get pregunta() { return i18nT('utils.asistenteLucia.que_tenemos_pendiente_los_consentimiento', '¿Qué tenemos pendiente? Los consentimientos sin firmar, las citas sin confirmar o por arreglar y los recalls.') } }),
])

// EL ANCHO, a gusto de cada persona (arrastrando el borde izquierdo), entre 380 y 640 px; se guarda en su navegador.
export const ANCHO_PANEL = Object.freeze({ min: 380, max: 640, defecto: 440, paso: 16 })
export function anchoValido(v) {
  const n = Math.round(Number(v))
  if (!Number.isFinite(n)) return ANCHO_PANEL.defecto
  return Math.min(ANCHO_PANEL.max, Math.max(ANCHO_PANEL.min, n))
}
export const claveAncho = (usuarioId) => 'biango:lucia-panel-ancho:' + (usuarioId == null ? 'yo' : String(usuarioId))
export function leerAncho(almacen, usuarioId) {
  try { const v = almacen && almacen.getItem(claveAncho(usuarioId)); return v == null ? ANCHO_PANEL.defecto : anchoValido(v) } catch (_) { return ANCHO_PANEL.defecto }
}
export function guardarAncho(almacen, usuarioId, ancho) {
  try { if (almacen) almacen.setItem(claveAncho(usuarioId), String(anchoValido(ancho))) } catch (_) { /* navegador sin almacén: se queda en esta sesión */ }
}

// t452 · AMPLIAR EL PANEL A TODO EL ANCHO (24-oct-2026). El dueño: «un botón en los ordenadores, a la izquierda del menú que se
// abre del asistente LucIA, para que ocupe todo el ancho de la pantalla cuando el usuario quiera. Lógicamente responsive, por si
// estoy tratando cosas que sean anchas, como informes con gráficos y eso».
//  · Ampliado, el panel ocupa la ventana entera menos su `margen`; la conversación va centrada a un ancho de `lectura` cómodo y
//    lo ancho (las gráficas, las tablas, «cómo vamos», lo pendiente) usa todo el ancho que tiene.
//  · Esc o el botón «Volver al panel» lo dejan como estaba. Se recuerda por persona en su navegador (como el ancho).
//  · Solo en el ordenador y en la tablet tumbada: en el móvil (< 900 px) el panel ya es la pantalla entera y no hay botón.
export const PANEL_AMPLIADO = Object.freeze({ margen: 16, lectura: 820 })
export const claveAmpliado = (usuarioId) => 'biango:lucia-panel-ampliado:' + (usuarioId == null ? 'yo' : String(usuarioId))
export function leerAmpliado(almacen, usuarioId) {
  try { return !!(almacen && almacen.getItem(claveAmpliado(usuarioId)) === '1') } catch (_) { return false }
}
export function guardarAmpliado(almacen, usuarioId, si) {
  try { if (almacen) almacen.setItem(claveAmpliado(usuarioId), si === true ? '1' : '0') } catch (_) { /* navegador sin almacén: vale para esta sesión */ }
}
// Lo que mide de ancho la conversación cuando el navegador todavía no lo ha medido (o no sabe: las guardias). Lo de verdad lo
// mide el panel con un ResizeObserver; esto es la misma cuenta con los rellenos del panel, para el primer pintado.
export function anchoConversacionCalculado({ anchoVentana, anchoPanel, ampliado = false, movil = false }) {
  const v = Number(anchoVentana) > 0 ? Number(anchoVentana) : 1280
  if (movil) return Math.max(240, Math.round(v - 24))
  if (ampliado) return Math.max(240, Math.round(v - 2 * PANEL_AMPLIADO.margen - 2 - 48))
  return Math.max(240, Math.round(Math.min(anchoValido(anchoPanel), v) - 28))
}
// El ancho que tienen las tarjetas de una respuesta: a todo el ancho de la conversación cuando van debajo de la burbuja (el
// móvil y el panel ampliado) y, dentro de la burbuja, lo que dejan la cara de LucIA (30 + 8) y el relleno de la burbuja (26).
export function anchoDeLasTarjetas({ anchoConversacion, ampliado = false, movil = false }) {
  const a = Math.max(240, Math.round(Number(anchoConversacion) || 0))
  return ampliado || movil ? a : Math.max(240, a - 64)
}
// EN COLUMNAS CUANDO HAY SITIO: cada cosa en su columna, de `min` px o más, y las que no caben bajan. En el panel de siempre y en
// el móvil sale una sola columna, como antes; ampliado, varias. Sin medir nada: lo reparte el navegador.
export const columnasQueCaben = (min = 280) => ({ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, ' + Math.round(Number(min) || 280) + 'px), 1fr))',
  columnGap: '20px', rowGap: '8px', alignItems: 'start', minWidth: 0 })
// Las tarjetas que son ANCHAS (una gráfica con su tabla, «cómo vamos», lo pendiente, lo de hoy): en el panel ampliado van a todo el
// ancho; las demás (un paciente, una cita, un presupuesto…) van en columnas dentro del ancho de lectura.
export const TARJETAS_ANCHAS = Object.freeze(['serie', 'comparativa', 'pendientes', 'avisos_dia', 'informe_medida'])   // t505 · el informe a medida, con su tabla

// LA ANIMACIÓN: entra deslizando desde la derecha (en el móvil, desde abajo) con un resorte suave y un poco de desvanecido;
// sale al revés y más rápido. Solo transform y opacity (no repinta la app). Con «reducir movimiento», solo el desvanecido.
export const ANIMACION_PANEL = Object.freeze({ abrirMs: 320, cerrarMs: 200, resorte: 'cubic-bezier(0.22, 1.08, 0.36, 1)', salida: 'cubic-bezier(0.4, 0, 1, 1)' })
export function estiloAnimacion({ visible, movil, reducido }) {
  const A = ANIMACION_PANEL
  const fuera = reducido ? 'none' : (movil ? 'translate3d(0, 100%, 0)' : 'translate3d(100%, 0, 0)')
  const dentro = reducido ? 'none' : 'translate3d(0, 0, 0)'
  const transition = visible
    ? (reducido ? `opacity ${A.abrirMs}ms ease-out` : `transform ${A.abrirMs}ms ${A.resorte}, opacity ${Math.round(A.abrirMs * 0.7)}ms ease-out`)
    : (reducido ? `opacity ${A.cerrarMs}ms ease-in` : `transform ${A.cerrarMs}ms ${A.salida}, opacity ${A.cerrarMs}ms ease-in`)
  return { transform: visible ? dentro : fuera, opacity: visible ? 1 : 0, transition }
}
export function prefiereMenosMovimiento(w) {
  try { return !!(w && typeof w.matchMedia === 'function' && w.matchMedia('(prefers-reduced-motion: reduce)').matches) } catch (_) { return false }
}

// LOS DÍAS de la conversación: «Hoy», «Ayer» o la fecha (el servidor dice qué día es hoy en la clínica).
export function etiquetaDia(dia, { hoy, ayer } = {}) {
  const d = String(dia || '')
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return ''
  if (d === hoy) return i18nT('utils.asistenteLucia.hoy', 'Hoy')
  if (d === ayer) return i18nT('utils.asistenteLucia.ayer', 'Ayer')
  return d.slice(8, 10) + '/' + d.slice(5, 7) + '/' + d.slice(0, 4)
}

// Las iniciales de un nombre (la tarjeta de un paciente sin foto).
export function inicialesDe(nombre) {
  const p = String(nombre || '').replace(/[^\p{L}\p{N}\s]/gu, ' ').trim().split(/\s+/).filter(Boolean)
  return ((p[0] ? p[0][0] : '') + (p[1] ? p[1][0] : '')).toUpperCase() || '?'
}

// ── t345 · LO QUE HAY ABIERTO EN LA PANTALLA (punto 3): solo ids, que el servidor comprueba ─────────────────────
// De la dirección: la ficha de un paciente (/pacientes?paciente=ID, y su presupuesto abierto ?presupuesto=ID) y el día de la
// agenda (/agenda?fecha=AAAA-MM-DD; sin fecha, el de hoy). De utils/pantallaAbierta: la cita o el presupuesto de una ventana.
const idDe = (v) => (/^\d{1,10}$/.test(String(v == null ? '' : v)) ? Number(v) : null)
export function pantallaDeLaDireccion(pathname, search) {
  const p = String(pathname || '')
  let q
  try { q = new URLSearchParams(String(search || '')) } catch (_) { return {} }
  if (/^\/pacientes\/?$/.test(p)) {
    const pac = idDe(q.get('paciente'))
    if (!pac) return {}
    const pr = idDe(q.get('presupuesto'))
    return { paciente: pac, ...(pr ? { presupuesto: pr } : {}) }
  }
  if (/^\/agenda\/?$/.test(p)) {
    const f = String(q.get('fecha') || '')
    return /^\d{4}-\d{2}-\d{2}$/.test(f) ? { agenda: 1, fecha: f } : { agenda: 1 }
  }
  return {}
}
// Lo de la dirección y lo abierto en una ventana, juntos (lo abierto manda: es lo que está delante).
export function pantallaParaLucia(direccion, abierto) {
  const a = abierto || {}
  return { ...(direccion || {}), ...(a.cita ? { cita: a.cita } : {}), ...(a.presupuesto ? { presupuesto: a.presupuesto } : {}) }
}
export const clavePantalla = (p) => ['paciente', 'presupuesto', 'cita', 'agenda', 'fecha', 'usuario', 'hora'].map((k) => k + '=' + (p && p[k] != null ? p[k] : '')).join('&')

// ══════════════════════════════════════════════════════════════════════════════════════════════
//  t369 · FASE 6, «EN MÁS SITIOS» (21-oct-2026): desde la ficha y la agenda con su contexto, el atajo y el móvil
// ══════════════════════════════════════════════════════════════════════════════════════════════
//  Lo que no es pintar, aquí (lo cargan las guardias de verdad): backend/routes/asistente-lucia-en-mas-sitios.test.js.

// LOS SITIOS desde los que se abre con su contexto: la ficha de un paciente, una cita (su ventana, su ficha rápida o su menú
// de la agenda) y un hueco de la agenda («LucIA: dar cita aquí»). Cada uno trae sus sugerencias (las da el servidor, con los
// permisos de quien pregunta: backend/services/asistenteLuciaContexto · SUGERENCIAS) y su pista en el cuadro de escribir.
export const SITIOS_LUCIA = Object.freeze(['ficha', 'cita', 'hueco'])
export const PISTA_POR_SITIO = Object.freeze({
  get ficha() { return i18nT('utils.asistenteLucia.preguntale_por_este_paciente', 'Pregúntale por este paciente…') },
  get cita() { return i18nT('utils.asistenteLucia.preguntale_por_esta_cita', 'Pregúntale por esta cita…') },
  get hueco() { return i18nT('utils.asistenteLucia.ej_a_marta_una_limpieza', 'Ej.: a Marta, una limpieza') },
})

// LO QUE VIAJA COMO CONTEXTO, sin fiarse de nada: ids enteros y, para un hueco, la agenda (su `usuario`), el día y la hora,
// los tres juntos o ninguno. Lo comprueba otra vez el servidor (backend/services/asistenteLuciaContexto · limpiar).
const RE_DIA = /^\d{4}-\d{2}-\d{2}$/
const RE_HORA = /^([01]\d|2[0-3]):[0-5]\d$/
export function contextoLimpio(c) {
  const x = c && typeof c === 'object' ? c : {}
  const out = {}
  for (const k of ['paciente', 'cita', 'presupuesto']) { const n = idDe(x[k]); if (n) out[k] = n }
  const fecha = RE_DIA.test(String(x.fecha || '')) ? String(x.fecha) : null
  const hora = String(x.hora == null ? '' : x.hora).slice(0, 5)
  const usuario = idDe(x.usuario)
  if (fecha && usuario && RE_HORA.test(hora)) Object.assign(out, { agenda: 1, fecha, usuario, hora })
  else if (fecha && x.agenda) Object.assign(out, { agenda: 1, fecha })
  return out
}

// EL CONTEXTO PEDIDO (el del botón) manda mientras se sigue en la misma pantalla: la misma dirección y, en la ficha, el mismo
// paciente. Al ir a otra pantalla deja de valer y vuelve lo que hay abierto (pantallaParaLucia). Una ventana que se cierra
// (la de la cita, al pulsar su «Preguntar a LucIA») no lo borra: lo que manda es la página.
export function claveDeLaPagina(pathname, search) {
  let pac = ''
  try { pac = new URLSearchParams(String(search || '')).get('paciente') || '' } catch (_) { pac = '' }
  return String(pathname || '') + '|' + pac
}
export function pantallaConPedido(derivada, pedido, clavePagina) {
  if (pedido && pedido.clave === clavePagina && pedido.contexto && Object.keys(pedido.contexto).length) return { ...pedido.contexto }
  return derivada || {}
}

// ── EL ATAJO DE TECLADO ───────────────────────────────────────────────────────────────────────
// Ctrl + Espacio. En un Mac, ⌘ + Espacio es el buscador del sistema (Spotlight) y el navegador ni se entera, y Ctrl + Espacio
// cambia el idioma del teclado si hay más de uno: allí es Opción (⌥) + L (y Ctrl + Espacio también vale, si le llega).
// Solo esa combinación EXACTA: ni con Mayúsculas, ni con AltGr (que en Windows es Ctrl + Alt), ni al mantenerla pulsada, ni
// mientras se compone una letra con acento. Por eso escribiendo en un campo no salta nunca, salvo con ella.
export const ATAJO_LUCIA = Object.freeze({ get general() { return i18nT('utils.asistenteLucia.ctrl_espacio', 'Ctrl + Espacio') }, get mac() { return i18nT('utils.asistenteLucia.opcion_l', 'Opción (⌥) + L') } })
export function esMac(nav) {
  try {
    const n = nav || {}
    const p = String((n.userAgentData && n.userAgentData.platform) || n.platform || '')
    return /mac|iphone|ipad|ipod/i.test(p)
  } catch (_) { return false }
}
export const textoAtajoLucia = (nav) => (esMac(nav) ? ATAJO_LUCIA.mac : ATAJO_LUCIA.general)
export function esAtajoLucia(e, mac = false) {
  if (!e || e.isComposing === true || e.repeat === true || e.keyCode === 229) return false
  const espacio = e.code === 'Space' || e.key === ' ' || e.key === 'Spacebar'
  if (espacio && e.ctrlKey === true && !e.altKey && !e.shiftKey && !e.metaKey) return true
  return mac === true && e.code === 'KeyL' && e.altKey === true && !e.ctrlKey && !e.shiftKey && !e.metaKey
}

// ── EN EL MÓVIL: CERRARLO DESLIZANDO HACIA ABAJO ───────────────────────────────────────────────
// Desde la cabecera: hacia abajo 110 px, o 50 si es un golpe rápido (0,6 px por milisegundo). De lado o hacia arriba, nada.
// Deslizando se MINIMIZA (como «–»): lo escrito se queda para la próxima vez.
export const GESTO_CERRAR = Object.freeze({ distancia: 110, corta: 50, velocidad: 0.6 })
export function gestoCierra({ dy, dx = 0, ms = 1000 } = {}) {
  const y = Number(dy)
  const x = Math.abs(Number(dx) || 0)
  const t = Math.max(1, Number(ms) || 1)
  if (!Number.isFinite(y) || y <= 0 || x > y) return false
  if (y >= GESTO_CERRAR.distancia) return true
  return y >= GESTO_CERRAR.corta && y / t >= GESTO_CERRAR.velocidad
}

// ── t430 · «SÍ» Y «NO» A LO QUE LucIA HA PREPARADO, Y LA PROPUESTA LEÍDA EN VOZ ALTA ───────────────────
// Lo que cuenta como solo un «sí» o solo un «no» (las palabras de shared/asistente-lucia-confirmar.json y el mismo cálculo que el
// servidor, backend/services/asistenteLuciaConfirmar · respuestaCorta): 'si' | 'no' | null. El panel lo usa para MANDAR SOLO el
// «sí» (o el «no») dictado cuando hay una propuesta esperándolo. t451: el «sí» no hace nada (solo «Hacerlo»); el «no» la descarta.
const normalizarRespuesta = (s) => String(s == null ? '' : s).toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  .replace(/[^a-z0-9ñ\s]/g, ' ').replace(/\s+/g, ' ').trim()
const FRASES_RESPUESTA = [
  ...CONFIRMAR.si.map((f) => [f, 'si']), ...CONFIRMAR.no.map((f) => [f, 'no']), ...CONFIRMAR.relleno.map((f) => [f, 'relleno']),
].map(([f, k]) => [normalizarRespuesta(f).split(' '), k]).sort((a, b) => b[0].length - a[0].length)
export function respuestaCortaLucia(texto) {
  const pal = normalizarRespuesta(texto).split(' ').filter(Boolean)
  if (!pal.length || pal.length > (Number(CONFIRMAR.max_palabras) || 8)) return null
  let i = 0; let si = false; let no = false
  while (i < pal.length) {
    const f = FRASES_RESPUESTA.find(([w]) => w.every((x, j) => pal[i + j] === x))
    if (!f) return null
    if (f[1] === 'si') si = true; else if (f[1] === 'no') no = true
    i += f[0].length
  }
  if (si && !no) return 'si'
  if (no && !si) return 'no'
  return null
}
// La propuesta que espera un «sí»: la ÚNICA sin hacer de la última respuesta de LucIA (con dos o más, se pulsa en la que sea).
export function propuestaQueEspera(mensajes) {
  const ultima = [...(Array.isArray(mensajes) ? mensajes : [])].reverse().find((m) => m && m.rol === 'lucia')
  const pendientes = ((ultima && ultima.acciones) || []).filter((a) => a && (a.estado || 'propuesta') === 'propuesta')
  return pendientes.length === 1 ? pendientes[0] : null
}
// Lo que se lee en voz alta de una respuesta: la propuesta en una frase (la escribe Biango: tarjeta.en_voz, que acaba pidiendo el
// botón «Hacerlo»: t451, todo va solo con él) o, si no hay, la respuesta sin sus marcas (negrita, enlaces). Como mucho 600 caracteres.
export function textoParaLeer(m) {
  const a = propuestaQueEspera(m ? [m] : [])
  if (a && a.tarjeta && a.tarjeta.en_voz) return String(a.tarjeta.en_voz).slice(0, 600)
  return String((m && m.texto) || '').replace(/\[([^\]]+)\]\([^)]*\)/g, '$1').replace(/\*\*/g, '').replace(/\s+/g, ' ').trim().slice(0, 600)
}
// Con la voz del navegador (sin coste ni nada que salga de él), en español. Sin voz en el navegador, no hace nada.
export function leerEnVoz(texto, w) {
  try {
    const voz = w && w.speechSynthesis
    const Frase = w && w.SpeechSynthesisUtterance
    if (!voz || typeof Frase !== 'function' || !String(texto || '').trim()) return false
    voz.cancel()
    const f = new Frase(String(texto).slice(0, 600))
    f.lang = 'es-ES'
    voz.speak(f)
    return true
  } catch (_) { return false }
}
